import { useState, useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { FaMagnifyingGlass } from 'react-icons/fa6';

import { TableActionButton, OrderStatusLegend } from '../styles/StyledComponents';
import { checkPermission } from '../utils/permissions';
import { useAppContext } from '../contextApi/context';
import TableContainer from '../components/table/TableContainer';
import { formatDateAndTime } from '../utils/dates';
import { searchWorkOrders, getMaintenanceTypes } from '../services/OrdersService';
import { searchUsersByName } from '../services/UsersService';
import { ORDER_STATUS, getOrderStatusConfig } from '../constants/orderStatus';
import { getClientTypeLabel, getClientTypeOptions } from '../constants/orderClientTypes';

const OrderHistory = () => {
    // ----- Context/Hooks
    const { loggedUser } = useAppContext();

    // ----- State
    const [maintenanceTypes, setMaintenanceTypes] = useState([]);
    const [availableUsersForFilter, setAvailableUsersForFilter] = useState([]);
    const [filterTimer, setFilterTimer] = useState(null);

    // ----- Effects
    // Cargar tipos de mantenimiento al montar el componente
    useEffect(() => {
        const loadMaintenanceTypes = async () => {
            try {
                const types = await getMaintenanceTypes(loggedUser.token);
                setMaintenanceTypes(types);
            } catch (error) {
                console.error('Error al cargar tipos de mantenimiento:', error);
            }
        };

        if (loggedUser?.token) {
            loadMaintenanceTypes();
        }
    }, [loggedUser]);

    // ----- Data Handler
    const fetchWorkOrdersHistoryData = useCallback(async (token, page = 1, perPage = 10, sortField = null, filters = []) => {
        try {
            // Transformar filtros del formato del componente al formato de la API
            const filtersData = filters
                .filter((filter) => filter.value !== '' && filter.value !== null && filter.value !== undefined)
                .map((filter) => {
                    // Manejar diferentes tipos de valores según el tipo de filtro
                    let values;

                    // Para filtros de rango de fechas
                    if (filter.operation === 'between' && Array.isArray(filter.value)) {
                        values = filter.value;
                    }
                    // Para filtros múltiples (select múltiple, tags, etc.)
                    else if (Array.isArray(filter.value)) {
                        values = filter.value;
                    }
                    // Para valores únicos
                    else {
                        values = [filter.value];
                    }
                    if (filter.columnName === 'orderNumber' && values.length > 0) {
                        values = values.map((val) => {
                            const numValue = Number(val);
                            return isNaN(numValue) ? val : numValue;
                        });
                    }

                    return {
                        columnName: filter.columnName,
                        operation: filter.operation,
                        values: values,
                    };
                });

            // Verificar si hay filtro de status aplicado por el usuario
            const userStatusFilter = filtersData.find((filter) => filter.columnName === 'status');

            if (!userStatusFilter) {
                // Si no hay filtro de status del usuario, aplicar filtro por defecto
                const defaultStatusFilter = {
                    columnName: 'status',
                    operation: 'in',
                    values: [ORDER_STATUS.FINALIZED.value, ORDER_STATUS.DISCARDED.value],
                };
                filtersData.push(defaultStatusFilter);
            }

            // Establecer ordenamiento por defecto si no se especifica
            const sortParam = sortField || 'requestedDate,desc';

            // Realizar la llamada a la API con los filtros transformados
            const response = await searchWorkOrders(token, page, perPage, sortParam, filtersData);

            // Verificar si la respuesta tiene la estructura esperada
            if (response && response.content) {
                return {
                    content: response.content,
                    totalElements: response.totalElements,
                    currentPage: page,
                    totalPages: response.totalPages,
                    number: response.number,
                    size: response.size,
                    first: response.first,
                    last: response.last,
                };
            } else {
                return {
                    content: [],
                    totalElements: 0,
                    currentPage: page,
                    totalPages: 0,
                };
            }
        } catch (error) {
            return {
                content: [],
                totalElements: 0,
                currentPage: page,
                totalPages: 0,
            };
        }
    }, []);

    // ----- Actions

    // Función para buscar usuarios en el filtro
    const handleSearchUsersForFilter = useCallback(
        async (searchText) => {
            if (!searchText || searchText.trim() === '') {
                setAvailableUsersForFilter([]);
                return;
            }

            clearTimeout(filterTimer);

            const newTimer = setTimeout(async () => {
                try {
                    const response = await searchUsersByName(loggedUser.token, searchText);
                    const formattedUsers =
                        response?.map((user) => ({
                            value: user.id,
                            label: `${user.firstName} ${user.lastName} (${user.username})`,
                        })) || [];
                    setAvailableUsersForFilter(formattedUsers);
                } catch (error) {
                    console.error('Error al buscar usuarios:', error);
                    setAvailableUsersForFilter([]);
                }
            }, 500);

            setFilterTimer(newTimer);
        },
        [loggedUser, filterTimer]
    );

    // ----- Helper Functions
    const getOrderNumberWithStatus = (orderNumber, status) => {
        const config = getOrderStatusConfig(status);

        return (
            <div
                className='position-relative'
                style={{
                    paddingLeft: '20px',
                    display: 'inline-block',
                }}
            >
                <OverlayTrigger
                    delay={{ hide: 200, show: 300 }}
                    overlay={(props) => <Tooltip {...props}>Estado: {config.label}</Tooltip>}
                    placement='top'
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '0',
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: config.color,
                            transform: 'translateY(-50%)',
                        }}
                    />
                </OverlayTrigger>
                <span>{orderNumber}</span>
            </div>
        );
    };

    // ----- Table Configuration
    const Actions = ({ row }) => (
        <div className='d-flex'>
            {checkPermission(loggedUser.permissions, 'view_work_orders') && (
                <OverlayTrigger
                    delay={{ hide: 200, show: 300 }}
                    overlay={(props) => <Tooltip {...props}>Ver detalles de la orden</Tooltip>}
                    placement='top'
                >
                    <TableActionButton className='btn btn-primary'>
                        <Link
                            to={`/ordenes/detalles/${row.id}`}
                            state={{
                                orderId: row.id,
                            }}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                            <FaMagnifyingGlass />
                            Detalles
                        </Link>
                    </TableActionButton>
                </OverlayTrigger>
            )}
        </div>
    );

    const columns = [
        {
            name: 'N° Orden',
            selector: (row) => getOrderNumberWithStatus(row.orderNumber, row.status),
            sortable: true,
            sortField: 'orderNumber',
            width: '120px',
        },
        {
            name: 'Fecha de solicitud',
            selector: (row) => formatDateAndTime(row.requestedDate),
            sortable: true,
            sortField: 'requestedDate',
            width: '170px',
        },
        {
            name: 'Solicitante',
            selector: (row) => row.requesterUser,
            sortField: 'requesterUser',
            width: '270px',
        },
        {
            name: 'Placa',
            selector: (row) => row.vehiclePlate,
            sortable: true,
            sortField: 'vehiclePlate',
            width: '140px',
        },
        {
            name: 'Tipo de mantenimiento',
            selector: (row) => row.maintenanceTypeName,
            sortable: true,
            sortField: 'maintenanceTypeName',
            width: '210px',
        },
        {
            name: 'Tipo de cliente',
            selector: (row) => getClientTypeLabel(row.orderClientType),
            sortable: true,
            sortField: 'orderClientType',
            width: '170px',
        },
        {
            name: 'Acciones',
            cell: (row) => <Actions row={row} />,
        },
    ];

    // Filtros con useMemo para actualización dinámica
    const filters = useMemo(
        () => [
            {
                column: 'orderNumber',
                input: 'text',
                label: 'N° Orden',
                operation: 'equals',
                length: 20,
            },
            {
                column: 'status',
                input: 'select',
                label: 'Estado',
                options: [
                    { value: ORDER_STATUS.FINALIZED.value, text: ORDER_STATUS.FINALIZED.label },
                    { value: ORDER_STATUS.DISCARDED.value, text: ORDER_STATUS.DISCARDED.label },
                ],
                operation: 'equals',
            },
            {
                column: 'requester.id',
                input: 'autocomplete',
                label: 'Solicitante',
                operation: 'equals',
                placeholder: 'Buscar por nombre, apellido o usuario',
                searchHandler: handleSearchUsersForFilter,
                options: availableUsersForFilter,
                noOptionsText: 'Escribe para buscar',
            },
            {
                column: 'vehicle.plate',
                input: 'text',
                label: 'Placa',
                operation: 'like',
                length: 20,
            },
            {
                column: 'maintenanceType.name',
                input: 'select',
                label: 'Tipo de mantenimiento',
                options: maintenanceTypes.map((type) => ({
                    value: type.name,
                    text: type.name,
                })),
                operation: 'equals',
            },
            {
                column: 'orderClientType',
                input: 'select',
                label: 'Tipo de cliente',
                options: getClientTypeOptions(),
                operation: 'equals',
            },
            {
                column: 'requestedDate',
                input: 'dates',
                label: 'Fecha de solicitud',
                operation: 'between',
            },
        ],
        [maintenanceTypes, availableUsersForFilter, handleSearchUsersForFilter]
    );

    // ----- Verificación de permisos
    if (!loggedUser || !checkPermission(loggedUser.permissions, 'view_work_orders')) {
        return null;
    }

    // ----- Render
    return (
        <div className='container mt-5'>
            <div className='mb-3'>
                <Link to='/ordenes'>Volver a órdenes activas</Link>
            </div>
            <h1>Historial de Órdenes de Trabajo</h1>

            {/* Leyenda de estados del historial con styled-component */}
            <div className='d-flex align-items-center gap-4 mb-3 mt-3' style={{ fontSize: '14px' }}>
                <span className='d-flex align-items-center'>
                    <OrderStatusLegend color={ORDER_STATUS.FINALIZED.color} />
                    {ORDER_STATUS.FINALIZED.label}
                </span>
                <span className='d-flex align-items-center'>
                    <OrderStatusLegend color={ORDER_STATUS.DISCARDED.color} />
                    {ORDER_STATUS.DISCARDED.label}
                </span>
            </div>

            {/* Tabla del historial */}
            <TableContainer columns={columns} fetchDataHandler={fetchWorkOrdersHistoryData} filters={filters} />
        </div>
    );
};

export default OrderHistory;
